import { BaseDAO } from './baseDAO';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConstantMan } from '../constantMan';
import { ApiHelper } from '../apiHelper';
import { HttpHeaders } from '@angular/common/http';
import { MapBounds } from '../../../app/shared/app-buyflow-map/app-buyflow-map.component';
import { Moment } from 'moment';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { VesselUnavailability } from 'anchor-shared/lib/model/interface/vesselAvailabilityInterfaces';
import { CharterType } from 'anchor-shared/lib/model/enum/charter-type';
import { termAvailabilityResponse } from 'anchor-shared/lib/model/interface/termAvailabilityResponse';

@Injectable()
export class VesselAvailabilityDAO extends BaseDAO<string> {
  constructor(public readonly apiHelper: ApiHelper) {
    super(apiHelper, ConstantMan.API.RESOURCE.VESSEL_AVAILABILITY);
  }

  public getVesselsForMap(
    mapBounds: MapBounds,
    pageOffset: number,
    itemsPerPage: number,
    charterType: CharterType,
    filters: string,
    sortOrder: string,
    requestedDate?: Moment,
    toDate?: Moment,
  ): Observable<{ count: number; rows: Vessel[]; page?: number }> {
    let momentFormat = 'YYYY-MM-DD';

    let formattedDate;
    if (requestedDate) {
      formattedDate = requestedDate.format(momentFormat);
    }
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/get-available-for-bounds' +
      `/?nelat=${mapBounds.northEastLat}&nelng=${mapBounds.northEastLng}&swlat=${mapBounds.southWestLat}&swlng=${mapBounds.southWestLng}` +
      `&charterType=${charterType}` +
      `&pageOffset=${pageOffset}&limit=${itemsPerPage}` +
      `&filters=${filters}&sortOrder=${sortOrder}&date=${formattedDate}`;
    if (charterType === CharterType.TermCharter && toDate) {
      const formattedToDate = toDate.format(momentFormat);
      url = url + `&toDate=${formattedToDate}`;
    }
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data: any = {};
    if (navigator && navigator.userAgent) {
      data.userAgentText = navigator.userAgent;
    }
    return this.apiHelper.apiCall<{
      count: number;
      rows: Vessel[];
      page?: number;
    }>(url, method, null, data, headers);
  }

  public getFullUnavailables(
    vesselId: number,
  ): Observable<VesselUnavailability[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/charter-vessel/unavailability/' +
      vesselId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<VesselUnavailability[]>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  updateFullAvailabilitiesForVessel(vesselId: number): Observable<string> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/create-avail-for-vessel/' +
      vesselId;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<string>(url, method, null, data, headers);
  }

  deleteAvailabilitiesForVessel(vesselId: number): Observable<string> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/delete-avail-for-vessel/' +
      vesselId;
    const method = 'DELETE';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<string>(url, method, null, data, headers);
  }

  updateSingleDayAvailabilityForVessel(
    vesselId: number,
    date: string,
  ): Observable<void> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/update-single-day-avail-for-vessel/' +
      vesselId;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      vesselId: vesselId,
      date: date,
    };
    return this.apiHelper.apiCall<void>(url, method, null, data, headers);
  }

  getTermCharterPricing(
    vesselId: number,
    date?: string,
    toDate?: string,
    includeOptionalAddOns: boolean = false,
  ): Observable<termAvailabilityResponse> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/term-charter-pricing';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      vesselId,
      date,
      toDate,
      includeOptionalAddOns,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }

  getTermCharterOptions(
    vesselId: number,
    date?: string,
    toDate?: string,
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/term-charter-optional-add-ons';
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      vesselId,
      date,
      toDate,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }
}
